import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GetOrCreateScratchTemplateRequest, GetStyleTemplateRequest, GetStyleTemplateResponse,
  GetStyleTreeRequest, GetStyleTreeResponse, ProductName, RenderedOutputSummary,
  SaveStyleTemplateRequest, SituViewWebApiClient, StyleCategoryNode, StyleNode, TemplateStyle, ThemeType
} from 'situview-webapi-client';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ISituReelItemPickerDialogData, SituReelItemPickerDialogComponent } from '../../admin/situ-reels/situ-reel-item-picker-dialog/situ-reel-item-picker-dialog.component';
import { ScratchThemeUsageService } from './scratch-theme-usage.service';

export interface IScratchTemplateDialogData {
  companyId: string | undefined;
  baseTemplateId?: string;
  baseTemplateName?: string;
  product?: ProductName;
}

/** A set of themes that can be grabbed into the scratch template — a style template picked from
 *  browse, or the exact theme set that produced one of this user's past scratch renders. */
export interface IScratchSource {
  /** Identity only: a style template id, or a rendered output id for a past scratch. */
  templateId: string;
  templateName: string | undefined;
  homeName: string | undefined;
  styles: TemplateStyle[];
  /** Overrides the derived label when the source needs naming for itself (e.g. a past scratch). */
  label?: string;
  /** A past scratch render rebuilt from its recorded styles, rather than a live style template. */
  isPreviousScratch?: boolean;
}

export const ALL_THEMES_SOURCE = 'all';
export const POPULAR_THEMES_SOURCE = 'popular';
export const GARDENS_SOURCE = 'gardens';
const POPULAR_LIMIT = 12;

/**
 * Scratch pad: move themes in and out of the caller's scratch template. Themes are grabbed either from
 * the full theme tree, or from any number of "sources" — style templates of renders picked from
 * situBROWSE (the base the caller came in with, plus any other homes added along the way).
 * Closes with the scratch template id when the caller should render it.
 */
@Component({
  selector: 'scratch-template-dialog',
  templateUrl: './scratch-template-dialog.component.html',
  styleUrls: ['./scratch-template-dialog.component.scss']
})
export class ScratchTemplateDialogComponent extends SituBaseComponent {
  scratchId: string | undefined;
  inScratch: TemplateStyle[] = [];

  sources: IScratchSource[] = [];
  selectedSourceId: string = ALL_THEMES_SOURCE;
  readonly allThemesSource = ALL_THEMES_SOURCE;
  readonly popularThemesSource = POPULAR_THEMES_SOURCE;
  readonly gardensSource = GARDENS_SOURCE;

  categoryData: StyleCategoryNode[] = [];
  popularStyles: StyleNode[] = [];
  gardenStyles: StyleNode[] = [];
  loading: boolean = false;

  constructor(
    authService: AuthService,
    stateService: StateService,
    public dialogRef: MatDialogRef<ScratchTemplateDialogComponent, string>,
    @Inject(MAT_DIALOG_DATA) public data: IScratchTemplateDialogData,
    private situViewClient: SituViewWebApiClient,
    private snackBar: MatSnackBar,
    private themeUsage: ScratchThemeUsageService,
    private dialog: MatDialog) {
      super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }

  get selectedSource(): IScratchSource | undefined {
    return this.sources.find(x => x.templateId === this.selectedSourceId);
  }

  sourceLabel(source: IScratchSource): string {
    return source.label || source.homeName || source.templateName || 'Source';
  }

  isInScratch(styleId: string | undefined): boolean {
    return !!styleId && this.inScratch.some(x => x.id === styleId);
  }

  addFromSource(style: TemplateStyle): void {
    if (style?.id && !this.isInScratch(style.id)) {
      this.inScratch = [...this.inScratch, style];
    }
  }

  addFromTree(node: StyleNode): void {
    if (!node?.id || this.isInScratch(node.id)) {
      return;
    }
    this.inScratch = [...this.inScratch, this.toTemplateStyle(node)];
  }

  private toTemplateStyle(node: StyleNode): TemplateStyle {
    return new TemplateStyle({
      id: node.id,
      name: node.themeName,
      displayName: node.displayName,
      styleCategoryId: node.styleCategoryId,
      thumbnailUrl: node.thumbnailUrl,
      companyId: node.companyId
    });
  }

  remove(styleId: string | undefined): void {
    this.inScratch = this.inScratch.filter(x => x.id !== styleId);
  }

  takeAll(): void {
    this.selectedSource?.styles.forEach(style => this.addFromSource(style));
  }

  clearScratch(): void {
    this.inScratch = [];
  }

  removeSource(templateId: string): void {
    this.sources = this.sources.filter(x => x.templateId !== templateId);
    if (this.selectedSourceId === templateId) {
      this.selectedSourceId = this.sources.length > 0 ? this.sources[0].templateId : ALL_THEMES_SOURCE;
    }
  }

  /**
   * Pop up situBROWSE filtered to this user's scratch template — i.e. every render the scratch pad
   * has produced. The scratch template itself is mutable, so reading it back would only ever give
   * today's contents; instead each render records the styles it actually used at the time
   * (`sourceArtifacts[].styleId`), which is what lets a past experiment be recovered.
   * Picking one loads it as a source to grab from, leaving the current scratch untouched.
   */
  viewPreviousScratches(): void {
    if (!this.scratchId) {
      return;
    }
    const pickerRef = this.dialog.open(SituReelItemPickerDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '85vh',
      data: {
        title: 'Previous scratches',
        styleConfigTemplateId: this.scratchId
      } as ISituReelItemPickerDialogData
    });

    pickerRef.afterClosed().subscribe((picked: RenderedOutputSummary | undefined) => {
      if (!picked?.id) {
        return;
      }
      this.addSourceFromPreviousScratch(picked);
    });
  }

  /** Rebuild the theme set behind a past scratch render from the styles recorded against it. */
  private addSourceFromPreviousScratch(picked: RenderedOutputSummary): void {
    const existing = this.sources.find(x => x.templateId === picked.id);
    if (existing) {
      this.selectedSourceId = existing.templateId;
      return;
    }

    // Styles resolve against the tree, so an unloaded tree would look like "everything's gone".
    if (this.categoryData.length === 0) {
      this.showMessage('Themes are still loading. Try that again in a moment.');
      return;
    }

    const styleIds = (picked.sourceArtifacts ?? [])
      .map(artifact => artifact?.styleId)
      .filter((styleId): styleId is string => !!styleId);

    // Resolving against the live tree means styles deleted or hidden since simply drop off.
    const styles = this.resolveStyles(styleIds);
    if (styles.length === 0) {
      this.showMessage(styleIds.length === 0
        ? 'That render didn\'t record which themes it used, so it can\'t be reloaded.'
        : 'None of the themes behind that render are available any more.');
      return;
    }

    this.sources = [...this.sources, {
      templateId: picked.id as string,
      // Deliberately not the template name — that's this user's internal scratch template.
      templateName: undefined,
      homeName: picked.homeConfig?.home,
      styles,
      label: this.previousScratchLabel(picked),
      isPreviousScratch: true
    }];
    this.selectedSourceId = picked.id as string;
  }

  private previousScratchLabel(picked: RenderedOutputSummary): string {
    const home = picked.homeConfig?.home;
    const date = picked.dateCreated ? picked.dateCreated.toLocaleDateString() : '';
    return [home, date].filter(part => !!part).join(' • ') || 'Previous scratch';
  }

  private resolveStyles(styleIds: string[]): TemplateStyle[] {
    const byId = this.buildStyleIndex();
    const styles: TemplateStyle[] = [];
    styleIds.forEach(styleId => {
      const node = byId.get(styleId);
      if (node) {
        styles.push(this.toTemplateStyle(node));
      }
    });
    return styles;
  }

  // Pop up situBROWSE to pick another home/render whose themes can be grabbed from.
  addSourceFromBrowse(): void {
    const pickerRef = this.dialog.open(SituReelItemPickerDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '85vh'
    });

    pickerRef.afterClosed().subscribe(async (picked: RenderedOutputSummary | undefined) => {
      const templateId = picked?.styleConfigTemplate?.id;
      if (!templateId) {
        return;
      }
      await this.addSource(templateId, picked?.styleConfigTemplate?.name, picked?.homeConfig?.home);
    });
  }

  async renderScratch(): Promise<void> {
    if (!this.scratchId) {
      return;
    }

    this.loading = true;
    const request = new SaveStyleTemplateRequest();
    request.styleTemplateId = this.scratchId;
    request.styles = this.inScratch.filter(x => !!x?.id).map(x => x.id as string);

    try {
      const response = await this.situViewClient.saveStyleTemplate(request);
      if (!response.success) {
        this.loading = false;
        this.showMessage('Could not save your scratch template. Please try again.');
        return;
      }
      // Remember what this user actually renders with, so their go-to themes surface next time.
      this.themeUsage.record(this.usageScope, request.styles);
      this.dialogRef.close(this.scratchId);
    } catch {
      this.loading = false;
      this.showMessage('Could not save your scratch template. Please try again.');
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private async init(): Promise<void> {
    this.loading = true;
    try {
      const scratchRequest = new GetOrCreateScratchTemplateRequest();
      scratchRequest.companyId = this.data?.companyId;
      // SituView has no user directory, so it names the template from what we tell it.
      scratchRequest.userName = this.authService.getUserName();
      const scratchResponse = await this.situViewClient.getOrCreateScratchTemplate(scratchRequest);

      if (!scratchResponse.success || !scratchResponse.styleTemplateId) {
        this.loading = false;
        this.showMessage('Could not open your scratch template. Please try again.');
        return;
      }

      this.scratchId = scratchResponse.styleTemplateId;
      this.inScratch = await this.loadTemplateStyles(this.scratchId);

      // The template the caller came in with becomes the first source to grab from.
      if (this.data?.baseTemplateId) {
        await this.addSource(this.data.baseTemplateId, this.data.baseTemplateName, undefined);
      }

      this.loadAllThemes();
    } catch {
      this.showMessage('Could not open your scratch template. Please try again.');
    }
    this.loading = false;
  }

  private async addSource(templateId: string, templateName: string | undefined, homeName: string | undefined): Promise<void> {
    const existing = this.sources.find(x => x.templateId === templateId);
    if (existing) {
      this.selectedSourceId = existing.templateId;
      return;
    }

    const styles = await this.loadTemplateStyles(templateId);
    this.sources = [...this.sources, { templateId, templateName, homeName, styles }];
    this.selectedSourceId = templateId;
  }

  private async loadTemplateStyles(templateId: string): Promise<TemplateStyle[]> {
    const request = new GetStyleTemplateRequest();
    request.styleTemplateId = templateId;
    const response: GetStyleTemplateResponse = await this.situViewClient.getStyleTemplate(request);
    return (response.success && response.styles)
      ? [...response.styles].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : [];
  }

  private loadAllThemes(): void {
    const request = new GetStyleTreeRequest();
    request.companyId = this.data?.companyId;
    request.showAll = true;
    this.situViewClient.getStyleTree(request)
      .then((response: GetStyleTreeResponse) => {
        this.categoryData = (response.success && response.categories)
          ? response.categories.filter(category => !!category && (category.styles?.length ?? 0) > 0)
          : [];
        this.refreshPopular();
        this.refreshGardens();
      })
      .catch(() => {
        this.categoryData = [];
      });
  }

  /**
   * Gardens published from situSCAPE arrive here automatically: publishing builds a RenderService
   * asset module named after the garden, which SituView turns into a LandscapingThemes Style under
   * a "Landscaping" category — so they're already in the style tree, just buried. Surface them.
   */
  private refreshGardens(): void {
    const gardens: StyleNode[] = [];
    this.categoryData.forEach(category =>
      (category.styles ?? []).forEach(style => {
        if (style?.themeType === ThemeType.LandscapingThemes) {
          gardens.push(style);
        }
      }));
    this.gardenStyles = gardens;
  }

  private buildStyleIndex(): Map<string, StyleNode> {
    const byId = new Map<string, StyleNode>();
    this.categoryData.forEach(category =>
      category.styles?.forEach(style => {
        if (style?.id) {
          byId.set(style.id, style);
        }
      }));
    return byId;
  }

  private get usageScope(): string {
    return ScratchThemeUsageService.scopeKey(this.authService.getClaim('sub'), this.data?.companyId);
  }

  /**
   * Resolve this user's most-used theme ids against the live tree. Resolving (rather than storing
   * names) means themes that have since been deleted or hidden simply drop off the list.
   */
  private refreshPopular(): void {
    const byId = this.buildStyleIndex();

    this.popularStyles = this.themeUsage.top(this.usageScope, POPULAR_LIMIT)
      .map(id => byId.get(id))
      .filter((style): style is StyleNode => !!style);

    // Nothing to grab from and no base? Start them on their go-to themes.
    if (this.popularStyles.length > 0 && this.sources.length === 0 && this.selectedSourceId === ALL_THEMES_SOURCE) {
      this.selectedSourceId = POPULAR_THEMES_SOURCE;
    }
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}
